import { motion } from "framer-motion";
import { ClassImportListType, StudentImportListType } from "utils/interfaces";
import { capitaliseFirstChar, singlify } from "utils/utils";
import ProcessingDataAnimation from "./utils/ProcessingDataAnimation";
import { usePlatform } from "components/utils/PlatformContext";
import { MdError } from "react-icons/md";

interface ReviewImportProps {
  importOption: "replace" | "create" | "update";
  reviewImportState: "processing" | "submit";
  csvErrors: string[][];
  newStudents: StudentImportListType[];
  newClasses: ClassImportListType[];
  existingStudents: StudentImportListType[];
  existingClasses: ClassImportListType[];
  otherSchoolStudents: StudentImportListType[];
}

const ReviewImport: React.FC<ReviewImportProps> = ({
  importOption,
  reviewImportState,
  csvErrors,
  newStudents,
  newClasses,
  existingStudents,
  existingClasses,
  otherSchoolStudents,
}) => {
  // Students from outside school table header definition
  const tableHeader = ["First name", "Last name", "Username"];

  // Define the body for the students from outside school
  const body =
    otherSchoolStudents && otherSchoolStudents.length > 0
      ? otherSchoolStudents.map((student: StudentImportListType) => [
          student.firstName,
          student.lastName,
          student.username,
        ])
      : [[]];

  // Get the platform context
  const { dataRequirements, schoolTerminology } = usePlatform();

  /**
   * Function executed when clicking "Download CSV of failed records"
   */
  const handleDownloadCSVFailedRecords = () => {
    // Define the header
    const header = dataRequirements.includes("heritage")
      ? "First name,Last name,DOB,Gender,Class name,Aboriginal or Torres Strait Islander orgin (Y/N),User name,Notes,Report\n"
      : "First name,Last name,DOB,Gender,Class name,User name,Notes,Report\n";

    // Transform every row into a string
    const content = csvErrors.map((row: string[]) => row.join(","));
    // And transform every row into a string
    const contentString = content.join("\n");

    // Create a Blob with the CSV data
    const blob = new Blob([header + contentString], {
      type: "text/csv;charset=utf-8;",
    });

    // Create a link element
    const link = document.createElement("a");

    // Create a URL for the blob
    const url = URL.createObjectURL(blob);

    // Set link attributes
    link.href = url;
    link.download = "import-failed-records.csv";

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // And finally revoke the URL to free up memory
    URL.revokeObjectURL(url);
  };

  /**
   * Function executed when clicking "View all new & modified classes"
   */
  const handleViewModifiedClasses = () => {
    // Define the header
    const header = "Class name,Status\n";

    // Transform every row into a string
    const newClassesArray = newClasses.map(
      (classItem: ClassImportListType) => `${classItem},New class`
    );
    const existingClassesArray = existingClasses.map(
      (classItem: ClassImportListType) =>
        `${classItem},${capitaliseFirstChar(importOption)} class`
    );
    // And transform every row into a string
    const newClassesString = newClassesArray.join("\n");
    const existingClassesString = existingClassesArray.join("\n");

    // Create a Blob with the CSV data
    const blob = new Blob(
      [header + newClassesString + "\n" + existingClassesString],
      {
        type: "text/csv;charset=utf-8;",
      }
    );

    // Create a link element
    const link = document.createElement("a");

    // Create a URL for the blob
    const url = URL.createObjectURL(blob);

    // Set link attributes
    link.href = url;
    link.download = "new-modified-classes.csv";

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // And finally revoke the URL to free up memory
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
      transition={{ duration: 0.1, ease: "easeOut" }}
      className="self-stretch w-[437px] flex-col justify-start items-start gap-8 inline-flex overflow-y-auto custom-scroll"
    >
      {reviewImportState === "submit" ? (
        <div className="h-[590px] flex-col justify-start items-start gap-8 inline-flex">
          <div className="self-stretch text-neutral-700 text-sm font-semibold font-sans leading-[17px]">
            Submit your import?
          </div>
          <div className="self-stretch text-neutral-700 text-sm font-normal font-sans leading-[17px]">
            Here is a summary of all actions following import:
          </div>
          <div className="self-stretch h-fit flex-col justify-start items-start gap-5 flex">
            <div className="self-stretch h-fit flex-col justify-start items-start gap-0.5 flex">
              <div className="self-stretch justify-start items-center gap-1 inline-flex">
                <div className="w-2 h-2 m-0.5 bg-neutral-700 rounded-full" />
                <div className="grow shrink basis-0">
                  <span className="text-neutral-700 text-sm font-normal font-sans leading-[21px]">
                    Number of new students:{" "}
                  </span>
                  <span className="text-neutral-700 text-sm font-bold font-sans leading-[21px]">
                    {(newStudents && newStudents.length) || 0}
                  </span>
                </div>
              </div>
              {csvErrors && csvErrors.length > 0 && (
                <div className="self-stretch justify-end items-end gap-2 inline-flex">
                  <div className="grow shrink basis-0 text-error-600 text-xs font-normal font-sans leading-[14px]">
                    {csvErrors.length || 0} of{" "}
                    {(csvErrors.length || 0) +
                      ((newStudents && newStudents.length) || 0) +
                      ((existingStudents && existingStudents.length) || 0)}{" "}
                    students were skipped due to errors.
                  </div>
                  <div
                    className="text-error-600 hover:text-error-800 text-xs font-semibold font-sans underline leading-[14px] cursor-pointer select-none"
                    onClick={handleDownloadCSVFailedRecords}
                  >
                    Download CSV of failed records
                  </div>
                </div>
              )}
            </div>
            {["replace", "update"].includes(importOption) && (
              <div className="self-stretch justify-start items-center gap-1 inline-flex">
                <div className="w-2 h-2 m-0.5 bg-neutral-700 rounded-full" />
                <div className="grow shrink basis-0">
                  <span className="text-neutral-700 text-sm font-normal font-sans leading-[21px]">
                    Number of students{" "}
                    {importOption === "replace"
                      ? "replaced"
                      : importOption === "update"
                      ? "updated"
                      : ""}
                    :{" "}
                  </span>
                  <span className="text-neutral-700 text-sm font-bold font-sans leading-[21px]">
                    {(existingStudents && existingStudents.length) || 0}
                  </span>
                </div>
              </div>
            )}
            <div className="self-stretch justify-start items-center gap-1 inline-flex">
              <div className="w-2 h-2 m-0.5 bg-neutral-700 rounded-full" />
              <div className="grow shrink basis-0">
                <span className="text-neutral-700 text-sm font-normal font-sans leading-[21px]">
                  Number of new classes:{" "}
                </span>
                <span className="text-neutral-700 text-sm font-bold font-sans leading-[21px]">
                  {(newClasses && newClasses.length) || 0}
                </span>
              </div>
            </div>
            {["replace", "update"].includes(importOption) && (
              <div className="self-stretch justify-start items-center gap-1 inline-flex">
                <div className="w-2 h-2 m-0.5 bg-neutral-700 rounded-full" />
                <div className="grow shrink basis-0">
                  <span className="text-neutral-700 text-sm font-normal font-sans leading-[21px]">
                    Number of classes{" "}
                    {importOption === "replace"
                      ? "replaced"
                      : importOption === "update"
                      ? "updated"
                      : ""}
                    :{" "}
                  </span>
                  <span className="text-neutral-700 text-sm font-bold font-sans leading-[21px]">
                    {(existingClasses && existingClasses.length) || 0}
                  </span>
                </div>
              </div>
            )}
            <div
              className="text-accent-600 hover:text-accent-700 text-sm font-normal font-sans underline leading-[17px] cursor-pointer select-none"
              onClick={handleViewModifiedClasses}
            >
              View all new & modified classes
            </div>
          </div>
          {otherSchoolStudents && otherSchoolStudents.length > 0 && (
            <div className="self-stretch bg-error-100 rounded-md py-2 px-3 text-neutral-700 text-sm font-normal font-sans leading-[17px] inline-flex flex-col gap-3">
              <span>
                <MdError className="inline-flex w-5 h-5 fill-error-600" /> The
                following students are enrolled at another {singlify(schoolTerminology).toLowerCase()} and cannot be
                modified:
              </span>
              <div className="w-full text-[10px] font-sans border rounded-md overflow-y-auto custom-scroll bg-white">
                <table className="w-full table-auto border">
                  <thead className="sticky top-0">
                    <tr className="bg-neutral-600">
                      {tableHeader.map((field: string, index: number) => (
                        <th
                          key={index}
                          className="text-left text-white px-2 border-x"
                        >
                          {field}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {body &&
                      body.length > 0 &&
                      body.map((row: string[], index: number) => (
                        <tr key={index} className="border border-dashed">
                          {row &&
                            row.length > 0 &&
                            row.map((value: string, indexValue: number) => (
                              <td key={indexValue} className="px-2 border-x">
                                {value}
                              </td>
                            ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : reviewImportState === "processing" ? (
        <ProcessingDataAnimation
          finishedValidation={false}
          processingText="Importing students and groups"
        />
      ) : (
        <></>
      )}
    </motion.div>
  );
};

export default ReviewImport;
